import { isBrand, type Brand } from './brands';

/** Dev-only, per-tab. sessionStorage so two tabs can hold different brands. */
const DEV_BRAND_KEY = 'numtable.devBrand';

/**
 * Brand is decided by HOSTNAME, never by route.
 *
 *   procalendar.app  -> 'procalendar'   (ClientShell, end clients)
 *   anything else    -> 'numtable'      (AppShell, the professional)
 *
 * Must stay in sync with the inline script in index.html, which runs the same
 * logic before the bundle loads to avoid a flash of the wrong brand.
 */
export function detectBrand(
  hostname: string = window.location.hostname,
  search: string = window.location.search,
): Brand {
  if (import.meta.env.DEV && isDevHost(hostname)) {
    const override = readDevOverride(search);
    if (override) return override;
  }
  const host = hostname.toLowerCase().split(':')[0];
  return host === 'procalendar.app' || host === 'www.procalendar.app' ? 'procalendar' : 'numtable';
}

/**
 * localhost can never be procalendar.app, so `?brand=procalendar` switches the
 * shell during development. The choice sticks for the rest of the tab session,
 * because in-app navigation drops the query string.
 *
 * Guarded by BOTH `import.meta.env.DEV` and a dev hostname — in a production
 * build the parameter is inert. Documented in STRUCTURE.md.
 */
function readDevOverride(search: string): Brand | null {
  const fromQuery = new URLSearchParams(search).get('brand');
  if (isBrand(fromQuery)) {
    try {
      sessionStorage.setItem(DEV_BRAND_KEY, fromQuery);
    } catch {
      // Not fatal: the override just won't survive the next navigation.
    }
    return fromQuery;
  }

  try {
    const remembered = sessionStorage.getItem(DEV_BRAND_KEY);
    if (isBrand(remembered)) return remembered;
  } catch {
    // Storage unavailable — fall back to hostname detection.
  }
  return null;
}

function isDevHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local')
  );
}
